import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import CustomerHeader from '../components/CustomerHeader';
import CustomAlert from '../components/CustomAlert';
import LoadingSpinner from '../components/LoadingSpinner';
import { FaCheckCircle, FaTools, FaExchangeAlt, FaTruck, FaTint, FaTimesCircle, FaClock, FaArrowRight } from 'react-icons/fa';
import dynamicEntityService from '../services/dynamicEntityService';
import cdeFountain from '../assets/images/Fountain.jpg';
import cdeCooler from '../assets/images/Fridge.jpeg';

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
`;

const ContentContainer = styled.div`
  padding: 1rem;
  max-width: 800px;
  margin: 0 auto;
`;

const PageTitle = styled.h2`
  color: ${props => props.theme.primary};
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const CDEGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const CDECard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const CDEImage = styled.div`
  width: 100%;
  height: 150px;
  background: #f8f9fa;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  border: 1px solid ${props => props.theme.border};
`;

const CDEType = styled.div`
  background: ${props => props.theme.primary};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-size: 0.85rem;
  display: inline-block;
  margin-bottom: 0.5rem;
`;

const CDEInfo = styled.div`
  margin-bottom: 0.5rem;
`;

const CDELabel = styled.span`
  font-weight: 600;
  color: ${props => props.theme.textSecondary};
  font-size: 0.85rem;
`;

const CDEValue = styled.span`
  color: ${props => props.theme.textPrimary};
  font-size: 0.9rem;
  display: block;
  margin-top: 0.25rem;
`;

const VerificationBadge = styled.div<{ verificationStatus: number }>`
  position: absolute;
  top: 0;
  right: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem 0.5rem 1.2rem;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  z-index: 10;

  /* Arrow edge shape using clip-path */
  clip-path: polygon(
    0 0,
    100% 0,
    100% 100%,
    15% 100%,
    0 50%
  );

  ${props => {
    if (props.verificationStatus === 1) {
      // Verified (Closed)
      return `
        background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        color: white;
        box-shadow: -2px 2px 8px rgba(76, 175, 80, 0.4);
      `;
    } else if (props.verificationStatus === 2) {
      // Under Verification
      return `
        background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
        color: white;
        box-shadow: -2px 2px 8px rgba(33, 150, 243, 0.4);
      `;
    } else {
      // Not Verified (0 or empty)
      return `
        background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
        color: white;
        box-shadow: -2px 2px 8px rgba(255, 152, 0, 0.4);
      `;
    }
  }}

  svg {
    font-size: 0.9rem;
  }
`;

const ServiceOptionsContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SelectedCDEInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
`;

const SelectedCDEImageContainer = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 8px;
  padding: 0.5rem;
  border: 1px solid #dee2e6;
`;

const SelectedCDEDetails = styled.div`
  flex: 1;
`;

const SmallVerificationBadge = styled.div<{ verificationStatus: number }>`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-radius: 4px;
  margin-top: 0.5rem;

  ${props => {
    if (props.verificationStatus === 1) {
      // Verified (Closed)
      return `
        background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
        color: white;
        box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
      `;
    } else if (props.verificationStatus === 2) {
      // Under Verification
      return `
        background: linear-gradient(135deg, #2196f3 0%, #1976d2 100%);
        color: white;
        box-shadow: 0 2px 4px rgba(33, 150, 243, 0.3);
      `;
    } else {
      // Not Verified (0 or empty)
      return `
        background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
        color: white;
        box-shadow: 0 2px 4px rgba(255, 152, 0, 0.3);
      `;
    }
  }}

  svg {
    font-size: 0.75rem;
  }
`;

const ServiceOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceOption = styled.button`
  background: white;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    border-color: ${props => props.theme.primary};

    svg {
      color: white;
    }
  }
`;

const ServiceIcon = styled.div`
  font-size: 2rem;
  color: ${props => props.theme.primary};
`;

const ServiceName = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid ${props => props.theme.primary};
  color: ${props => props.theme.primary};
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
`;

const AddNewCDEButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;

  &:hover {
    background: ${props => props.theme.primaryDark};
  }
`;

const RaiseRequestButton = styled.button`
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  padding: 0.6rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s;

  &:hover {
    background: ${props => props.theme.primaryDark};
  }

  svg {
    font-size: 0.9rem;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const SecondaryActionButton = styled.button`
  background: transparent;
  color: ${props => props.theme.primary};
  border: 2px solid ${props => props.theme.primary};
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  flex: 1;

  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
`;

interface CDEUnit {
  id: string;
  serialNumber: string;
  manufacturerSerial: string;
  model: string;
  type: 'Cooler' | 'Fountain';
  cdeImage: string;
  description: string;
  status: 'verified' | 'unverified' | 'pending';
  isVerified: number; // 0 = Not Verified, 1 = Verified, 2 = Under Verification
  verificationStatus: string;
}

const VerifyCDE: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cdeUnits, setCDEUnits] = useState<CDEUnit[]>([]);
  const [selectedCDE, setSelectedCDE] = useState<CDEUnit | null>(null);
  const [showServiceOptions, setShowServiceOptions] = useState(false);
  const [alert, setAlert] = useState<{
    isVisible: boolean;
    type?: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
  }>({ isVisible: false, message: '' });

  useEffect(() => {
    loadCDEUnits();
  }, []);
const cdeImages: Record<string, string> = {
  'Water Dispenser': '/images/water_dispenser.png',
  'Fountain': cdeFountain,
  'Cooler': cdeCooler,
  'Default': '../assets/images/cde_plus_logo.png'
};
  const loadCDEUnits = async () => {
    setIsLoading(true);
    try {
      const greenTag = localStorage.getItem('greenTagNumber') || localStorage.getItem('customerNumber');

      if (!greenTag) {
        setAlert({
          isVisible: true,
          type: 'warning',
          title: 'No Customer',
          message: 'No customer information found. Please login again.'
        });
        navigate('/');
        return;
      }

      // Call verify_cde entity through dynamic API
      const response = await dynamicEntityService.getEntityData(
        'verify_cde',
        greenTag,
        {} // No specific serial number, get all equipment for this customer
      );

      if (response.success && response.data) {
        // Transform the data into CDEUnit format
        const units: CDEUnit[] = response.data.map((item: any, index: number) => {
          // Determine CDE type based on various fields
          let cdeType = 'Equipment';
          const rawType = item.object_type || item.material || item.cde_type || '';

          // Map various type indicators to Cooler or Fountain
          if (rawType) {
            const typeStr = rawType.toString().toLowerCase();
            if (typeStr.includes('fountain') || typeStr.includes('dispenser') || typeStr.includes('bib')) {
              cdeType = 'Fountain';
            } else if (typeStr.includes('cooler') || typeStr.includes('refrigerator') || typeStr.includes('chiller')) {
              cdeType = 'Cooler';
            } else {
              // Default based on common naming patterns
              cdeType = rawType;
            }
          }

          return {
            id: item.id || String(index + 1),
            serialNumber: item.serial_no || '',
            manufacturerSerial: item.manufacturer_serial_no || item.manufact_serial_number || '',
            model: item.model_no || '',
            type: cdeType,
            cdeImage: cdeImages[cdeType] || cdeImages['Default'],
            description: item.description || item.description2 || 'CDE Equipment',
            status: item.verification_type || 'verified',
            isVerified: Number(item.ISVerified) || 0, // 0 = Not Verified, 1 = Verified, 2 = Under Verification
            verificationStatus: item.verification_status || ''
          };
        });

        setCDEUnits(units);

        if (units.length === 0) {
          setAlert({
            isVisible: true,
            type: 'info',
            title: 'No Equipment Found',
            message: 'No equipment registered for this customer.'
          });
        }
      } else {
        // If no data, show empty state
        setCDEUnits([]);
        setAlert({
          isVisible: true,
          type: 'info',
          title: 'No Equipment',
          message: response.message || 'No equipment found for this customer.'
        });
      }
    } catch (error) {
      console.error('Error loading CDE units:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to load CDE units. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCDESelect = (cde: CDEUnit) => {
    setSelectedCDE(cde);
    setShowServiceOptions(true);
  };

  const handleServiceOptionSelect = (serviceType: string) => {
    if (!selectedCDE) return;

    // Navigate to appropriate form based on service type
    switch (serviceType) {
      case 'cde-service':
        navigate('/raise-service-request?type=CDE_SERVICE', {
          state: { cdeUnit: selectedCDE, serviceType: 'CDE_SERVICE' }
        });
        break;
      case 'pull-out':
        navigate('/raise-service-request?type=PULLOUT_REQUEST', {
          state: { cdeUnit: selectedCDE, serviceType: 'PULLOUT_REQUEST' }
        });
        break;
      case 'relocation':
        navigate('/unit-relocation', {
          state: { cdeUnit: selectedCDE }
        });
        break;
      case 'water-sample':
        navigate('/raise-service-request?type=WATER_SAMPLING', {
          state: { cdeUnit: selectedCDE, serviceType: 'WATER_SAMPLING' }
        });
        break;
      default:
        break;
    }
  };

  const handleAddNewCDE = () => {
    navigate('/');
  };

  const handleBack = () => {
    setShowServiceOptions(false);
    setSelectedCDE(null);
  };

  // Function to verify a specific serial number
  const verifyCDEBySerial = async (serialNo: string) => {
    setIsLoading(true);
    try {
      const greenTag = localStorage.getItem('greenTagNumber') || localStorage.getItem('customerNumber');

      if (!greenTag) {
        setAlert({
          isVisible: true,
          type: 'warning',
          title: 'No Customer',
          message: 'No customer information found. Please login again.'
        });
        return;
      }

      // Call verify_cde entity with specific serial number
      const response = await dynamicEntityService.getEntityData(
        'verify_cde',
        greenTag,
        { serial_no: serialNo }
      );

      if (response.success && response.data && response.data.length > 0) {
        const equipment = response.data[0];
        const verificationType = equipment.verification_type;

        if (verificationType === 'success') {
          setAlert({
            isVisible: true,
            type: 'success',
            title: 'Equipment Verified',
            message: equipment.verification_status
          });
        } else if (verificationType === 'warning') {
          setAlert({
            isVisible: true,
            type: 'warning',
            title: 'Equipment Status',
            message: equipment.verification_status
          });
        } else {
          setAlert({
            isVisible: true,
            type: 'error',
            title: 'Verification Failed',
            message: equipment.verification_status
          });
        }

        // Reload all units to show updated list
        await loadCDEUnits();
      } else {
        setAlert({
          isVisible: true,
          type: 'error',
          title: 'Not Found',
          message: `No equipment found with serial number: ${serialNo}`
        });
      }
    } catch (error) {
      console.error('Error verifying CDE:', error);
      setAlert({
        isVisible: true,
        type: 'error',
        title: 'Error',
        message: 'Failed to verify equipment. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <CustomerHeader skipAuthCheck={true} />
      {isLoading && <LoadingSpinner isVisible={isLoading} />}

      <ContentContainer>
        {!showServiceOptions ? (
          <>
            <PageTitle>My Coca-Cola CDE</PageTitle>

            <CDEGrid>
              {cdeUnits.map((cde) => (
                <CDECard key={cde.id}>
                  <VerificationBadge verificationStatus={cde.isVerified}>
                    {cde.isVerified === 1 ? (
                      <>
                        {React.createElement(FaCheckCircle as any, {})}
                        Verified
                      </>
                    ) : cde.isVerified === 2 ? (
                      <>
                        {React.createElement(FaClock as any, {})}
                        Under Verification
                      </>
                    ) : (
                      <>
                        {React.createElement(FaTimesCircle as any, {})}
                        Not Verified
                      </>
                    )}
                  </VerificationBadge>

                  <CDEImage>
                    <img src={cde.cdeImage} alt={cde.type} style={{ width: '40%', height: 'auto' }} />
                  </CDEImage>

                  <CDEType>{cde.type}</CDEType>

                  <CDEInfo>
                    <CDELabel>{cde.description}</CDELabel>
                  </CDEInfo>

                  <CDEInfo>
                    <CDELabel>Serial No:</CDELabel>
                    <CDEValue>{cde.serialNumber}</CDEValue>
                  </CDEInfo>

                  <CDEInfo>
                    <CDELabel>Model:</CDELabel>
                    <CDEValue>{cde.model}</CDEValue>
                  </CDEInfo>

                  <CDEInfo>
                    <CDELabel>CDE Type:</CDELabel>
                    <CDEValue>{cde.type}</CDEValue>
                  </CDEInfo>

                  <RaiseRequestButton onClick={() => handleCDESelect(cde)}>
                    Raise Service Request
                    {React.createElement(FaArrowRight as any, {})}
                  </RaiseRequestButton>
                </CDECard>
              ))}
            </CDEGrid>

            <AddNewCDEButton onClick={handleAddNewCDE}>
              Add New CDE
            </AddNewCDEButton>
          </>
        ) : (
          <>
            <BackButton onClick={handleBack}>← Back to CDE List</BackButton>
            <PageTitle>My Coca-Cola CDE</PageTitle>

            <ServiceOptionsContainer>
              <SelectedCDEInfo>
                <SelectedCDEImageContainer>
                  <img
                    src={selectedCDE?.cdeImage}
                    alt={selectedCDE?.type}
                    style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                  />
                </SelectedCDEImageContainer>
                <SelectedCDEDetails>
                  <h3 style={{ marginBottom: '0.5rem', color: '#dc3545' }}>
                    {selectedCDE?.description}
                  </h3>
                  <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
                    Serial No: {selectedCDE?.serialNumber} | Model: {selectedCDE?.model}
                  </div>
                  <SmallVerificationBadge verificationStatus={selectedCDE?.isVerified || 0}>
                    {selectedCDE?.isVerified === 1 ? (
                      <>
                        {React.createElement(FaCheckCircle as any, {})}
                        Verified
                      </>
                    ) : selectedCDE?.isVerified === 2 ? (
                      <>
                        {React.createElement(FaClock as any, {})}
                        Under Verification
                      </>
                    ) : (
                      <>
                        {React.createElement(FaTimesCircle as any, {})}
                        Not Verified
                      </>
                    )}
                  </SmallVerificationBadge>
                </SelectedCDEDetails>
              </SelectedCDEInfo>

              <ServiceOptionsGrid>
                <ServiceOption onClick={() => handleServiceOptionSelect('cde-service')}>
                  <ServiceIcon>
                    {React.createElement(FaTools as any, {})}
                  </ServiceIcon>
                  <ServiceName>CDE Service</ServiceName>
                </ServiceOption>

                <ServiceOption onClick={() => handleServiceOptionSelect('pull-out')}>
                  <ServiceIcon>
                    {React.createElement(FaTruck as any, {})}
                  </ServiceIcon>
                  <ServiceName>Pull-Out Request</ServiceName>
                </ServiceOption>

                <ServiceOption onClick={() => handleServiceOptionSelect('relocation')}>
                  <ServiceIcon>
                    {React.createElement(FaExchangeAlt as any, {})}
                  </ServiceIcon>
                  <ServiceName>CDE Relocation</ServiceName>
                </ServiceOption>

                {selectedCDE?.type === 'Fountain' && (
                  <ServiceOption onClick={() => handleServiceOptionSelect('water-sample')}>
                    <ServiceIcon>
                      {React.createElement(FaTint as any, {})}
                    </ServiceIcon>
                    <ServiceName>Water Sampling</ServiceName>
                  </ServiceOption>
                )}
              </ServiceOptionsGrid>

              <ActionButtonsContainer>
                {selectedCDE?.isVerified !== 1 && (
                  <SecondaryActionButton onClick={() => navigate('/cde-verification')}>
                    Reverify CDE
                  </SecondaryActionButton>
                )}
                <SecondaryActionButton onClick={handleAddNewCDE}>
                  Add New CDE
                </SecondaryActionButton>
              </ActionButtonsContainer>
            </ServiceOptionsContainer>
          </>
        )}
      </ContentContainer>

      <CustomAlert
        isVisible={alert.isVisible}
        type={alert.type}
        title={alert.title}
        message={alert.message}
        onClose={() => setAlert({ isVisible: false, message: '' })}
      />
    </PageContainer>
  );
};

export default VerifyCDE;